import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(isoWeek);

// Chart picker range.
// grain=day, offsetDays=0: complete calendar days through today.
// grain=day, offsetDays=1: complete calendar days through yesterday (sales).
// Day queries send YYYY-MM-DD (same labels as merchant_sales_daily.day).
// grain=hour: last complete hour back N*24h (product usage, ISO instants).
export const CHART_MAX_RANGE_DAYS = 30;
export const CHART_MAX_CALENDAR_DAYS = 31;
export const CHART_GRAIN_DAY = 'day';
export const CHART_GRAIN_HOUR = 'hour';

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

const ROLLING_PRESETS = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 14 days', value: '14' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Month to date', value: 'mtd' },
];

const CALENDAR_PRESETS = [
    { label: 'Last month', value: 'last_month' },
    { label: 'Current quarter', value: 'quarter' },
    { label: 'Last quarter', value: 'last_quarter' },
    { label: 'Current year', value: 'year' },
];

const startOfDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const endOfDay = (date) => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next;
};

const startOfHour = (date) => {
    const next = new Date(date);
    next.setMinutes(0, 0, 0);
    return next;
};

const sameDay = (left, right) =>
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();

const isMidnight = (date) =>
    date.getHours() === 0
    && date.getMinutes() === 0
    && date.getSeconds() === 0
    && date.getMilliseconds() === 0;

const isEndOfCalendarDay = (date) =>
    date.getHours() === 23 && date.getMinutes() === 59;

export const normalizeChartRangeOptions = ({
    grain = CHART_GRAIN_DAY,
    offsetDays = 0,
    maxRangeDays = CHART_MAX_RANGE_DAYS,
} = {}) => {
    const isHour = grain === CHART_GRAIN_HOUR;
    const max_days = Number(maxRangeDays);
    return {
        grain: isHour ? CHART_GRAIN_HOUR : CHART_GRAIN_DAY,
        offsetDays: isHour ? 0 : Math.max(0, Number(offsetDays) || 0),
        maxRangeDays: Number.isFinite(max_days) && max_days > 0 ? max_days : CHART_MAX_RANGE_DAYS,
    };
};

export const chartMaxEnd = (options, now = new Date()) => {
    const { grain, offsetDays } = normalizeChartRangeOptions(options);
    if (grain === CHART_GRAIN_HOUR) {
        return startOfHour(now);
    }
    const day = startOfDay(now);
    day.setDate(day.getDate() - offsetDays);
    return endOfDay(day);
};

const alignHourEnd = (date, maxEnd) => {
    if (date.getTime() >= maxEnd.getTime()) {
        return new Date(maxEnd);
    }
    const hour = startOfHour(date);
    if (hour.getTime() === date.getTime()) {
        return date;
    }
    const next = new Date(hour.getTime() + HOUR_MS);
    return next.getTime() > maxEnd.getTime() ? new Date(maxEnd) : next;
};

const rollingStart = (end, days, grain) => {
    if (grain === CHART_GRAIN_HOUR) {
        return new Date(end.getTime() - days * DAY_MS);
    }
    const start = startOfDay(end);
    start.setDate(start.getDate() - (days - 1));
    return start;
};

const singleDayStart = (end, grain) => {
    if (grain === CHART_GRAIN_HOUR) {
        const start = startOfDay(end);
        if (start.getTime() >= end.getTime()) {
            return new Date(end.getTime() - HOUR_MS);
        }
        return start;
    }
    return startOfDay(end);
};

const monthToDateStart = (end, grain, maxRangeDays) => {
    const monthStart = startOfDay(end);
    monthStart.setDate(1);
    const earliest = rollingStart(end, maxRangeDays, grain);
    return monthStart < earliest ? earliest : monthStart;
};

const calendarPresetBounds = (preset, end) => {
    const quarter_start = new Date(end.getFullYear(), Math.floor(end.getMonth() / 3) * 3, 1);
    const bounds = {
        last_month: () => [
            new Date(end.getFullYear(), end.getMonth() - 1, 1),
            new Date(end.getFullYear(), end.getMonth(), 0),
        ],
        quarter: () => [quarter_start, end],
        last_quarter: () => [
            new Date(quarter_start.getFullYear(), quarter_start.getMonth() - 3, 1),
            new Date(quarter_start.getFullYear(), quarter_start.getMonth(), 0),
        ],
        year: () => [new Date(end.getFullYear(), 0, 1), end],
    };
    const [start, bound_end] = bounds[preset]();
    return { start, end: bound_end };
};

export const chartRangeForPreset = (preset, options, now = new Date()) => {
    const opts = normalizeChartRangeOptions(options);
    const end = chartMaxEnd(opts, now);
    let start;
    if (CALENDAR_PRESETS.some((item) => item.value === preset)) {
        const bounds = calendarPresetBounds(preset, end);
        return { startDate: startOfDay(bounds.start), endDate: endOfDay(bounds.end) };
    }
    if (preset === 'yesterday' && opts.grain === CHART_GRAIN_DAY && opts.offsetDays === 0) {
        const day = startOfDay(end);
        day.setDate(day.getDate() - 1);
        return { startDate: day, endDate: endOfDay(day) };
    }
    if (preset === 'today' || preset === 'yesterday') {
        start = singleDayStart(end, opts.grain);
    } else if (preset === 'mtd') {
        start = monthToDateStart(end, opts.grain, opts.maxRangeDays);
    } else {
        const days = Number(preset);
        start = rollingStart(end, Number.isFinite(days) && days > 0 ? days : opts.maxRangeDays, opts.grain);
    }
    if (opts.grain === CHART_GRAIN_DAY) {
        return { startDate: startOfDay(start), endDate: endOfDay(end) };
    }
    return { startDate: start, endDate: end };
};

export const clampChartRange = (range, options, now = new Date()) => {
    const opts = normalizeChartRangeOptions(options);
    const maxEnd = chartMaxEnd(opts, now);
    let startDate = new Date(range.startDate);
    let endDate = new Date(range.endDate);

    if (opts.grain === CHART_GRAIN_HOUR) {
        const pickedDays = isMidnight(startDate) && (isMidnight(endDate) || isEndOfCalendarDay(endDate));
        if (pickedDays) {
            startDate = startOfDay(startDate);
            const exclusive = startOfDay(endDate);
            exclusive.setDate(exclusive.getDate() + 1);
            endDate = exclusive;
        }
        endDate = alignHourEnd(endDate, maxEnd);
        const minStart = new Date(endDate.getTime() - opts.maxRangeDays * DAY_MS);
        if (startDate.getTime() < minStart.getTime()) {
            startDate = minStart;
        }
        if (startDate.getTime() >= endDate.getTime()) {
            startDate = new Date(endDate.getTime() - HOUR_MS);
        }
        return { startDate, endDate };
    }

    startDate = startOfDay(startDate);
    endDate = endOfDay(endDate);
    if (endDate.getTime() > maxEnd.getTime()) {
        endDate = endOfDay(maxEnd);
    }
    const minStart = rollingStart(endDate, opts.maxRangeDays, CHART_GRAIN_DAY);
    if (startDate.getTime() < minStart.getTime()) {
        startDate = minStart;
    }
    if (startDate.getTime() > endDate.getTime()) {
        startDate = startOfDay(endDate);
    }
    return { startDate, endDate };
};

const previousChartRange = (range, options) => {
    if (!range?.startDate || !range?.endDate) {
        return null;
    }
    const opts = normalizeChartRangeOptions(options);
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    if (opts.grain === CHART_GRAIN_HOUR) {
        const span = end.getTime() - start.getTime();
        return {
            startDate: new Date(start.getTime() - span),
            endDate: new Date(start),
        };
    }
    const from = startOfDay(start);
    const last = startOfDay(end);
    const days = Math.round((last.getTime() - from.getTime()) / DAY_MS) + 1;
    const previousEnd = new Date(from);
    previousEnd.setDate(previousEnd.getDate() - 1);
    const previousStart = new Date(previousEnd);
    previousStart.setDate(previousStart.getDate() - (days - 1));
    return { startDate: startOfDay(previousStart), endDate: endOfDay(previousEnd) };
};

const toCalendarDay = (date) => {
    const next = new Date(date);
    const year = next.getFullYear();
    const month = String(next.getMonth() + 1).padStart(2, '0');
    const day = String(next.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const chartQueryPeriods = (range, options) => {
    const opts = normalizeChartRangeOptions(options);
    const toQuery = ({ startDate, endDate }) => {
        if (opts.grain === CHART_GRAIN_HOUR) {
            return {
                from: new Date(startDate).toISOString(),
                to: new Date(endDate).toISOString(),
            };
        }
        return {
            from: toCalendarDay(startDate),
            to: toCalendarDay(endDate),
        };
    };
    return {
        current: toQuery(range),
        previous: toQuery(previousChartRange(range, options) ?? range),
    };
};

export const chartDateRangePresets = (options) => {
    const { offsetDays, grain, maxRangeDays } = normalizeChartRangeOptions(options);
    const today = { label: 'Today', value: 'today' };
    const yesterday = { label: 'Yesterday', value: 'yesterday' };
    const has_calendar_presets = grain === CHART_GRAIN_DAY && maxRangeDays > CHART_MAX_CALENDAR_DAYS;
    if (has_calendar_presets) {
        const days = offsetDays > 0 ? [yesterday] : [today, yesterday];
        return [...days, ...ROLLING_PRESETS, ...CALENDAR_PRESETS];
    }
    return [offsetDays > 0 ? yesterday : today, ...ROLLING_PRESETS];
};

export const chartPickerRanges = (options, now = new Date()) => {
    const ranges = {};
    chartDateRangePresets(options).forEach((preset) => {
        const { startDate, endDate } = chartRangeForPreset(preset.value, options, now);
        ranges[preset.label] = [startDate, endDate];
    });
    return ranges;
};

export const SERIES_PRESET_DEFAULT = '6m';

export const SERIES_PRESETS = [
    { value: 'year', label: 'Last year', grain: 'month' },
    { value: '6m', label: 'Last 6 months', grain: 'month' },
    { value: 'month', label: 'Last month', grain: 'day' },
    { value: '14', label: 'Last 14 days', grain: 'day' },
    { value: '7', label: 'Last week', grain: 'day' },
];

export const seriesQueryForPreset = (preset, now = new Date()) => {
    const end = chartMaxEnd({ grain: CHART_GRAIN_DAY, offsetDays: 0 }, now);
    const grain = SERIES_PRESETS.find((item) => item.value === preset)?.grain || 'month';
    const start = new Date(end);
    if (preset === 'year') {
        start.setMonth(start.getMonth() - 11, 1);
    } else if (preset === '6m') {
        start.setMonth(start.getMonth() - 5, 1);
    } else if (preset === 'month') {
        start.setDate(start.getDate() - 29);
    } else if (preset === '14') {
        start.setDate(start.getDate() - 13);
    } else {
        start.setDate(start.getDate() - 6);
    }
    return {
        from: toCalendarDay(startOfDay(start)),
        to: toCalendarDay(end),
        grain,
    };
};

export const WEEK_SERIES_PRESET_DEFAULT = '26w';

export const WEEK_SERIES_PRESETS = [
    { value: '13w', label: 'Last 13 weeks', weeks: 13 },
    { value: '26w', label: 'Last 26 weeks', weeks: 26 },
    { value: '52w', label: 'Last 52 weeks', weeks: 52 },
];

export const QUARTER_SERIES_PRESET_DEFAULT = '8q';

export const QUARTER_SERIES_PRESETS = [
    { value: '4q', label: 'Last 4 quarters', quarters: 4 },
    { value: '8q', label: 'Last 8 quarters', quarters: 8 },
    { value: '12q', label: 'Last 12 quarters', quarters: 12 },
];

export const seriesQueryForQuarterPreset = (preset, now = new Date()) => {
    const match = QUARTER_SERIES_PRESETS.find((item) => item.value === preset);
    const quarters = match?.quarters || 8;
    const end = chartMaxEnd({ grain: CHART_GRAIN_DAY, offsetDays: 0 }, now);
    const end_day = toCalendarDay(end);
    const end_month = dayjs.utc(end_day).startOf('month');
    const quarter_month = Math.floor(end_month.month() / 3) * 3;
    const start = end_month.month(quarter_month).startOf('month').subtract((quarters - 1) * 3, 'month');
    return {
        from: start.format('YYYY-MM-DD'),
        to: end_day,
        grain: 'quarter',
    };
};

export const seriesQueryForWeekPreset = (preset, now = new Date()) => {
    const match = WEEK_SERIES_PRESETS.find((item) => item.value === preset);
    const weeks = match?.weeks || 26;
    const end = chartMaxEnd({ grain: CHART_GRAIN_DAY, offsetDays: 0 }, now);
    const end_day = toCalendarDay(end);
    const start_monday = dayjs.utc(end_day).startOf('isoWeek').subtract(weeks - 1, 'week');
    return {
        from: start_monday.format('YYYY-MM-DD'),
        to: end_day,
        grain: 'week',
    };
};

export const SALESPEOPLE_PRESET_DEFAULT = 'month';
export const SALESPEOPLE_MAX_RANGE_DAYS = 31;
export const SALESPEOPLE_BOARD_ROTATE_MS = 30000;
export const SALESPEOPLE_BOARD_PAGE_SIZE = 100;
export const SALESPEOPLE_SESSION_REFRESH_MS = 10 * 60 * 1000;

export const SALESPEOPLE_PRESETS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'week', label: 'This week' },
    { value: 'month', label: 'This month' },
    { value: '30', label: 'Last 30 days' },
];

const addCalendarDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const shiftMonth = (date, months) => {
    const day = date.getDate();
    const shifted = new Date(date.getFullYear(), date.getMonth() + months, 1);
    const last_day = new Date(shifted.getFullYear(), shifted.getMonth() + 1, 0).getDate();
    shifted.setDate(Math.min(day, last_day));
    return startOfDay(shifted);
};

const salespeoplePresetWindows = {
    today(today) {
        const start = today;
        const previous = addCalendarDays(today, -1);
        return { start, end: today, previousStart: previous, previousEnd: previous };
    },
    yesterday(today) {
        const start = addCalendarDays(today, -1);
        const previous = addCalendarDays(today, -2);
        return { start, end: start, previousStart: previous, previousEnd: previous };
    },
    week(today) {
        const monday_offset = (today.getDay() + 6) % 7;
        const start = addCalendarDays(today, -monday_offset);
        return {
            start,
            end: today,
            previousStart: addCalendarDays(start, -7),
            previousEnd: addCalendarDays(today, -7),
        };
    },
    month(today) {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
            start,
            end: today,
            previousStart: shiftMonth(start, -1),
            previousEnd: shiftMonth(today, -1),
        };
    },
    30(today) {
        const start = addCalendarDays(today, -29);
        const previous_end = addCalendarDays(start, -1);
        return {
            start,
            end: today,
            previousStart: addCalendarDays(previous_end, -29),
            previousEnd: previous_end,
        };
    },
};

const toDaySpan = ({ start, end, previousStart, previousEnd }) => ({
    startDate: startOfDay(start),
    endDate: endOfDay(end),
    previousStart: startOfDay(previousStart),
    previousEnd: endOfDay(previousEnd),
});

export const isSalespeoplePreset = (preset) =>
    SALESPEOPLE_PRESETS.some((item) => item.value === preset);

export const salespeoplePresetLabel = (preset) =>
    SALESPEOPLE_PRESETS.find((item) => item.value === preset)?.label || '';

export const salespeoplePresetRange = (preset, now = new Date()) => {
    const today = startOfDay(now);
    const build = salespeoplePresetWindows[preset] || salespeoplePresetWindows.month;
    return toDaySpan(build(today));
};

export const salespeoplePresetQuery = (preset, now = new Date()) => {
    const span = salespeoplePresetRange(preset, now);
    return {
        current: {
            from: toCalendarDay(span.startDate),
            to: toCalendarDay(span.endDate),
        },
        previous: {
            from: toCalendarDay(span.previousStart),
            to: toCalendarDay(span.previousEnd),
        },
    };
};

export const salespeoplePickerRanges = (now = new Date()) => {
    const ranges = {};
    SALESPEOPLE_PRESETS.forEach((preset) => {
        const span = salespeoplePresetRange(preset.value, now);
        ranges[preset.label] = [span.startDate, span.endDate];
    });
    return ranges;
};

export const matchSalespeoplePreset = (range, now = new Date()) => {
    if (!range?.startDate || !range?.endDate) {
        return 'custom';
    }
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    const preset = SALESPEOPLE_PRESETS.find((item) => {
        const next = salespeoplePresetRange(item.value, now);
        return sameDay(next.startDate, start) && sameDay(next.endDate, end);
    });
    return preset?.value ?? 'custom';
};

export const matchChartPreset = (range, options, now = new Date()) => {
    if (!range?.startDate || !range?.endDate) {
        return 'custom';
    }
    const opts = normalizeChartRangeOptions(options);
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    const preset = chartDateRangePresets(opts).find((item) => {
        const next = chartRangeForPreset(item.value, opts, now);
        if (opts.grain === CHART_GRAIN_HOUR) {
            return next.startDate.getTime() === start.getTime() && next.endDate.getTime() === end.getTime();
        }
        return sameDay(next.startDate, start) && sameDay(next.endDate, end);
    });
    return preset?.value ?? 'custom';
};
